import axios from 'axios';

export class ContactUs {
  name: string;
  email: string;
  company: string;
  organization: string;
  organization_type: string;
  role: string;
  country_code: string;
  contact_number: string;
  contact_purpose: string;
  project_types: Array<string>;
  budget: string;
  ideal_go_live_date: string;
  project_description: string;
  project_description2: string;
  attachment_file: File | undefined;

  // Constructor for initializing the object
  constructor(
    name?: string,
    email?: string,
    company?: string,
    organization?: string,
    organization_type?: string,
    role?: string,
    country_code?: string,
    contact_number?: string,
    contact_purpose?: string,
    project_types?: Array<string>,
    budget?: string,
    ideal_go_live_date?: string,
    project_description?: string,
    project_description2?: string,
    attachment_file?: File
  ) {
    this.name = name || '';
    this.email = email || '';
    this.company = company || '';
    this.organization = organization || '';
    this.organization_type = organization_type || '';
    this.role = role || '';
    this.country_code = country_code || '';
    this.contact_number = contact_number || '';
    this.contact_purpose = contact_purpose || '';
    this.project_types = project_types || [];
    this.budget = budget || '';
    this.ideal_go_live_date = ideal_go_live_date || '';
    this.project_description = project_description || '';
    this.project_description2 = project_description2 || '';
    this.attachment_file = attachment_file;
  }


  isValidEmail(): boolean {
    if (this.email.trim().length === 0) {
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(this.email);
  }

  isValidName(): boolean {
    return this.name.trim().length > 0;
  }

  isValidCompanyName(): boolean {
    return this.company.trim().length > 0;
  }

  isValidOrganization(): boolean {
    return this.organization.trim().length > 0;
  }

  isValidOrganizationType(): boolean {
    return this.organization_type.trim().length > 0;
  }

  isValidRole(): boolean {
    return this.role.trim().length > 0;
  }
  isValidContactNumber(): boolean {
    // Check if contact number exists
    if (this.contact_number.trim().length === 0) {
      return false;
    }
    // Determine the minimum phone number length based on the country code
    const minimumPhoneNumberLength = this.country_code === '+60' ? 8 : 6;
    // Regular expression for numeric validation
    const phoneNumberRegex = new RegExp(`^\\d{${minimumPhoneNumberLength},}$`);
    // Validate contact number
    return phoneNumberRegex.test(this.contact_number.trim());
  }


  isValidContactPurpose(): boolean {
    return this.contact_purpose.trim().length > 0;
  }

  isValidProjectDescription(): boolean {
    const trimmedDescription = this.project_description.trim();
    const wordCount = trimmedDescription.split(/\s+/).filter(Boolean).length;
    return wordCount >= 10 && wordCount <= 250;
  }

  isValidProjectDescription2(): boolean {
    const trimmedDescription = this.project_description2.trim();
    const wordCount = trimmedDescription.split(/\s+/).filter(Boolean).length;
    return (wordCount >= 10 && wordCount <= 250) || wordCount === 0;
  }

  isValidBudget(): boolean {
    return this.budget.trim().length > 0;
  }

  isValidIdealGoLiveDate(): boolean {
    return this.ideal_go_live_date.trim().length > 0;
  }

  isValidProjectTypes(): boolean {
    return this.project_types.length > 0;
  }

  // Factory method to create a new ContactUs object
  static create(): ContactUs {
    if (process.env.APP_ENV === 'local') {
      // Fill with dummy values for local environment
      return new ContactUs(
        'John Doe', // name
        'john.doe@example.com', // email
        'Example Company', // company
        'Example Organization', // organization
        '', // organization_type
        'Developer', // role
        '+65', // country_code
        '12345678', // contact_number
        'I have a project idea', // contact_purpose
        ['Website', 'Mobile App'], // project_types
        'Medium: US$ 20,000 — US$ 50,000', // budget
        '31/01/2025', // ideal_go_live_date
        'This is a sample project description containing at least ten words for testing purposes.', // project_description
        'Here are some additional details to ensure the description exceeds ten words.', // project_description2
        undefined // attachment_file
      );
    }
    // Return an empty object for non-local environments
    return new ContactUs();
  }

  async sendEmailWithAttachments(recaptchaToken: string): Promise<void> {
    try {
      const sendEmailUrl = process.env.APP_URL + '/wp-json/contact-us/v1/submit';

      const formData = new FormData();
      formData.append('recaptchaToken', recaptchaToken);

      // Append email data to formData
      formData.append('message', JSON.stringify(this)); // Serialize the instance
      formData.append('organization', this.organization);

      // Append files to formData if available
      if (this.attachment_file) {
        formData.append('attachments[]', this.attachment_file, this.attachment_file.name);
      }

      // Make POST request to custom WordPress API endpoint with formData
      const response = await axios.post(sendEmailUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Handle response
      if (response.data.success) {
        console.log('Email sent successfully');
      } else {
        console.error('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
    }
  }

}
